import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import { useEffect, useState } from "react";

const componentTypeToCode = {
  Earning: "E",
  Deduction: "D",
  Contribution: "C",
  E: "E",
  D: "D",
  C: "C",
};

const calculationMethodToCode = {
  Fixed: "F",
  Percentage: "P",
  F: "F",
  P: "P",
};

const paymentTypeToCode = {
  Monthly: "M",
  Weekly: "W",
  M: "M",
  W: "W",
};

const initialForm = {
  code: "",
  name: "",
  type: "E",
  paymentType: "M",
  defaultAmount: 0,
  defaultPercentage: 0,
  taxable: true,
  calculationMethod: "F",
  earningCategory: "",
  remarks: "",
};

const componentTypes = [
  { value: "E", label: "Earning" },
  { value: "D", label: "Deduction" },
  { value: "C", label: "Contribution" },
];

const calculationMethods = [
  { value: "F", label: "Fixed" },
  { value: "P", label: "Percentage" },
  // { value: "M", label: "Manual" },
  // { value: "X", label: "System Calculated" },
];

const paymentTypes = [
  { value: "M", label: "Monthly" },
  { value: "W", label: "Weekly" },
];

const earningCategories = [
  { value: "", label: "None" },
  { value: "F", label: "Fixed" },
  { value: "V", label: "Variable" },
]

const inputClass = `
  w-full h-14
  rounded-md
  border border-surface-200
  bg-white
  px-3.5
  text-[16px] text-surface-800
  outline-none
  transition-colors
  focus:border-primary-500
  focus:ring-1 focus:ring-primary-500
`;

const errorClass = '!border-red-400 focus:!border-red-500 focus:!ring-red-500';

const labelClass = `
  absolute
  left-3
  top-0
  z-10
  -translate-y-1/2
  bg-white
  px-1.5
  text-[13px]
  text-surface-500
  pointer-events-none
`;

const maskCurrencyInput = (rawValue) => {
  const digits = String(rawValue ?? "").replace(/\D/g, "");

  if (!digits) return "";

  const padded = digits.padStart(3, "0");

  const cents = padded.slice(-2);

  const wholeDigits = padded
    .slice(0, -2)
    .replace(/^0+(?=\d)/, "");

  const whole = Number(wholeDigits || "0").toLocaleString("en-US");

  return `${whole}.${cents}`;
};

const parseCurrency = (value) => {
  if (value === "" || value === null || value === undefined) {
    return 0;
  }

  const n = Number(String(value).replace(/,/g, ""));

  return Number.isNaN(n) ? 0 : n;
};

const generateUniqueCode = (name, existingCodesArray) => {
  const existingCodes = new Set(existingCodesArray || []);
  const letters = name.replace(/[^A-Za-z]/g, "").toUpperCase();

  if (!letters) return "";

  // If the word itself if 4 letters or fewer, just use it (if unique)
  if (letters.length <= 4) {
    if (!existingCodes.has(letters)) return letters;
  }

  const first = letters[0];
  const last = letters[letters.length - 1];
  const middle = letters.slice(1, -1);

  // Attempt 1: First letter + next two letters + last letter
  let attempt = (first + middle.substring(0, 2) + last).substring(0,4);
  if (!existingCodes.has(attempt)) return attempt;

  // Attempt 2: First + random middle letters + last
  for (let i = 0; i < 50; i++) {
    let randomMid = "";
    for (let j = 0; j < Math.min(2, middle.length); j++) {
      randomMid += middle[Math.floor(Math.random() * middle.length)]
    }
    attempt = (first + randomMid + last).substring(0, 4);
    if (!existingCodes.has(attempt)) return attempt;
  }

  // Attempt 3: First + Random Alphaneumetic + Last
  const chars = "ABCDEFGHIJKLMNOPQRSTUVQXYZ0123456789";
  for (let i = 0; i < 50; i++) {
    attempt = (first + chars[Math.floor(Math.random() * chars)] + chars[Math.floor(Math.random() * chars)] + last).substring(0, 4);
    if (!existingCodes.has(attempt)) return attempt;
  }

  return attempt;
}

function Field({ label, children, error = "", }) {
  return (
    <div className="relative">
      <label className={labelClass}>{label}</label>

      {children}

      {error && (
        <p className="mt-1.5 flex items-start gap-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  type = "text",
  min,
  step,
  error = "",
  disabled = false,
}) {
  return (
    <Field label={label} error={error}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        min={min}
        step={step}
        disabled={disabled}
        className={`${inputClass} ${
          error ? errorClass : ""
        } ${
          disabled
            ? "!bg-gray-100 !text-gray-500 cursor-not-allowed"
            : ""
        }`}
      />
    </Field>
  );
}

function SelectInput({ label, value, onChange, children, error = "", disabled = false, }) {
  return (
    <Field label={label} error={error}>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`${inputClass} appearance-auto cursor-pointer ${
          error ? errorClass : ""
        } ${
          disabled ? "!bg-gray-100 !text-gray-500 cursor-not-allowed" : ""
        }`}
      >
        {children}
      </select>
    </Field>
  );
}

function SwitchInput({ label, checked, onChange, disabled = false, }) {
  return (
    <div className={`flex h-14 items-center rounded-md border border-surface-200 bg-white px-3.5 ${
      disabled ? "bg-gray-100" : "bg-white"
    }`}>
      <label className="flex cursor-pointer items-center gap-3 text-[16px] text-surface-800">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="
            h-5 w-5
            cursor-pointer
            rounded
            border-surface-300
            accent-primary-600
          "
        />
        <span>{label}</span>
      </label>
    </div>
  );
}

function CurrencyInput({
  label,
  value,
  onChange,
  disabled = false,
  error = "",
}) {
  return (
    <Field label={label} error={error}>
      <input
        type="text"
        inputMode="decimal"
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => {
          const maskedValue = maskCurrencyInput(event.target.value);

          onChange({
            target: {
              type: "text",
              value: maskedValue,
            },
          });
        }}
        className={`${inputClass} ${
          error ? errorClass : ""
        } ${
          disabled ? "!bg-gray-100 !text-gray-500 cursor-not-allowed" : ""
        }`}
      />
    </Field>
  );
}

export default function AddPayrollComponentDialog({
  open,
  onClose,
  onSave,
  loading = false,
  mode = "add",
  component = null,
  existingComponents = [],
}) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";
  const isReadOnly = isViewMode;

  useEffect(() => {
    if (!open) {
      return;
    }

    setErrors({});

    if (mode === "add" || !component) {
      setForm(initialForm);
      setCodeManuallyEdited(false);
      return;
    }

    const typeCode = componentTypeToCode[component.type] ?? "E";
    const calculationMethodCode = calculationMethodToCode[component.calcMethod] ?? "F";
    const paymentTypeCode = paymentTypeToCode[component.paymentType] ?? "M";

    setForm({
      code: component.code ?? "",
      name: component.name ?? "",
      type: typeCode,
      paymentType: paymentTypeCode,
      calculationMethod: calculationMethodCode,

      defaultAmount:
        component.defaultAmount !== null &&
        component.defaultAmount !== undefined
          ? maskCurrencyInput(Number(component.defaultAmount).toFixed(2))
          : "",

      defaultPercentage:
        component.defaultPercentage ??
        "",

      taxable:
        component.taxable === true ||
        component.taxable === 1 ||
        component.taxable === "1" ||
        component.taxable === "true",
      earningCategory: component.earningCategory ?? "",
      remarks: component.remarks ?? "",
    })
  }, [open, mode, component]);

  const handleChange = (field) => (e) => {
    if (isReadOnly) return;

    if (field === "code" && mode !== "add") {
      return;
    }

    const value =
      e.target.type === "checkbox"
        ? e.target.checked
        : e.target.value;

    // setForm((prev) => {
    //   if (field === "calculationMethod") {
    //     return {
    //       ...prev,
    //       calculationMethod: value,
    //       defaultAmount: value === "F" ? prev.defaultAmount : null,
    //       defaultPercentage: value === "P" ? prev.defaultPercentage : null,
    //     };
    //   }

    //   return {
    //     ...prev,
    //     [field]: value,
    //   }
    // });

    setForm((prev) => {
      let updated = { ...prev, [field]: value }

      if (field === "calculationMethod") {
        updated.calculationMethod = value;
        updated.defaultAmount = value === "F" ? prev.defaultAmount : null;
        updated.defaultPercentage = value === "P" ? prev.defaultPercentage : null;
      }

      if (field === "code") {
        setCodeManuallyEdited(true);
      }

      // Auto generate code when typing name if code was not manually edited
      if (field === "name" && mode === "add" && !codeManuallyEdited) {
        const existingCodes = existingComponents.map((c) => c.code);
        updated.code = generateUniqueCode(value, existingCodes);
      }

      return updated;
    })

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }))
  };

  const validate = () => {
    const newErrors = {};

    if (!form.code.trim()) {
      newErrors.code = "Component code is required.";
    } 
    
    // else if (form.code.trim().length !== 3) {
    //   newErrors.code = "Component code must be exactly 3 characters.";
    // }

    if (!form.name.trim()) {
      newErrors.name = "Component name is required.";
    }

    if (form.calculationMethod === "F" && parseCurrency(form.defaultAmount) < 0) {
      newErrors.defaultAmount = "Default amount cannot be negative.";
    }

    if (form.calculationMethod === "P") {
      if (Number(form.defaultPercentage) < 0) {
        newErrors.defaultPercentage = "Default percentage cannot be negative.";
      } else if (Number(form.defaultPercentage) > 100) {
        newErrors.defaultPercentage = "Default percentage cannot be more than 100.";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };


  const handleSave = () => {
    if (isViewMode) {
      return;
    }

    if (!validate()) {
      return;
    }

    onSave({
      ...form,
      id: component?.id,
      defaultAmount: form.calculationMethod === "F" ? parseCurrency(form.defaultAmount) : null,
      defaultPercentage: form.calculationMethod === "P" ? Number(form.defaultPercentage || 0) : null,
      earningCategory: form.earningCategory || null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className="!px-6 !pt-5 !pb-3">
        <span className="text-[21px] font-medium text-surface-900">
          {mode === "add" ? "New Payroll Component" : mode === "edit" ? "Edit Payroll Component" : "View Payroll Component"}
        </span>
      </DialogTitle>

      <DialogContent className="!px-6 !py-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

          {/* Component Code */}
          <TextInput
            label="Component Code"
            value={form.code}
            onChange={handleChange("code")}
            error={errors.code}
            disabled={mode !== "add"}
          />

          {/* Component Name */}
          <TextInput
            label="Component Name"
            value={form.name}
            error={errors.name}
            onChange={handleChange("name")}
            disabled={isReadOnly}
          />

          {/* Component Type */}
          <SelectInput
            label="Component Type"
            value={form.type}
            onChange={handleChange("type")}
            disabled={isReadOnly}
          >
            {componentTypes.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </SelectInput>

          <SelectInput
            label="Earning Category"
            value={form.earningCategory}
            onChange={handleChange("earningCategory")}
            disabled={isReadOnly}
          >
            {earningCategories.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </SelectInput>

          {/* Payment Type */}
          {/* <SelectInput
            label="Payment Type"
            value={form.paymentType}
            onChange={handleChange("paymentType")}
            disabled={isReadOnly}
          >
            {paymentTypes.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </SelectInput> */}

          {/* Calculation Method */}
          <SelectInput
            label="Calculation Method"
            value={form.calculationMethod}
            onChange={handleChange("calculationMethod")}
            disabled={isReadOnly}
          >
            {calculationMethods.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </SelectInput>

          {/* Default Amount */}
          {
            form.calculationMethod === "F" && (
              <CurrencyInput
                label="Default Amount"
                value={form.defaultAmount}
                error={errors.defaultAmount}
                disabled={isReadOnly}
                onChange={handleChange("defaultAmount")}
              />
            )
          }

          {/* Default Percentage */}
          {
            form.calculationMethod === "P" && (
              <TextInput
                label="Default Percentage"
                type="number"
                min="0"
                step="0.01"
                value={form.defaultPercentage ?? ""}
                error={errors.defaultPercentage}
                disabled={isReadOnly}
                onChange={handleChange("defaultPercentage")}
              />
            )
          }


          {/* Taxable */}
          <SwitchInput
            label="Taxable Component"
            checked={form.taxable}
            disabled={isReadOnly}
            onChange={handleChange("taxable")}
          />

          {/* Remarks */}
          {/* <div className="relative sm:col-span-2">
            <label className={labelClass}>Remarks</label>

            <textarea
              value={form.remarks}
              onChange={handleChange("remarks")}
              disabled={isReadOnly}
              rows={3}
              className={`
                w-full
                rounded-md
                border border-surface-200
                bg-white
                px-3.5
                py-3
                text-[16px] text-surface-800
                outline-none
                transition-colors
                focus:border-primary-500
                focus:ring-1 focus:ring-primary-500
                disabled:cursor-not-allowed
                disabled:bg-gray-100
                disabled:text-gray-500
              `}
            />
          </div> */}

          <TextInput
            label="Remarks"
            value={form.remarks}
            onChange={handleChange("remarks")}
            disabled={isReadOnly}
          />

        </div>
      </DialogContent>

      <DialogActions className="!px-6 !py-3">
        <Button
          onClick={onClose}
          disabled={loading}
          className="!text-primary-600"
        >
          {isViewMode ? "CLOSE" : "CANCEL"}
        </Button>

        {!isViewMode && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            className="!bg-primary-600 hover:!bg-primary-700"
          >
            {loading ? "SAVING..." : "SAVE"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
